<template>
  <div class="pos-skeleton" aria-hidden="true">
    <div
      v-if="showHeader"
      class="align-items-xl-end d-flex flex-column flex-xl-row justify-content-between mb-3 gap-2"
    >
      <div class="pos-skeleton__pills" v-if="pillCount > 0">
        <span
          v-for="n in pillCount"
          :key="`sk-pill-${n}`"
          class="pos-skeleton__pill pos-skeleton__shimmer"
          :style="{ width: pillWidths[(n - 1) % pillWidths.length] }"
        ></span>
      </div>
      <div class="pos-skeleton__search" v-if="variant === 'bills'">
        <span class="pos-skeleton__search-bar pos-skeleton__shimmer"></span>
      </div>
      <div class="pos-skeleton__badges" v-else-if="badgeCount > 0">
        <span
          v-for="n in badgeCount"
          :key="`sk-badge-${n}`"
          class="pos-skeleton__badge pos-skeleton__shimmer"
        ></span>
      </div>
    </div>

    <div v-if="variant === 'bills'" class="row g-2 mb-3">
      <div
        v-for="n in summaryCardCount"
        :key="`sk-sum-${n}`"
        class="col-6 col-md-3"
      >
        <div class="pos-skeleton__summary-card">
          <div class="pos-skeleton__line pos-skeleton__line--sm pos-skeleton__shimmer" style="width: 55%"></div>
          <div class="pos-skeleton__line pos-skeleton__line--xl pos-skeleton__shimmer mt-2" style="width: 70%"></div>
        </div>
      </div>
    </div>

    <div v-if="variant === 'bills'" class="pos-skeleton__datatable">
      <div class="pos-skeleton__row pos-skeleton__row--head" :style="rowGridStyle">
        <div
          v-for="c in columnCount"
          :key="`sk-head-${c}`"
          class="pos-skeleton__cell"
        >
          <div
            class="pos-skeleton__line pos-skeleton__line--sm pos-skeleton__shimmer"
            style="width: 60%"
          ></div>
        </div>
      </div>
      <div
        v-for="n in rowCount"
        :key="`sk-row-${n}`"
        class="pos-skeleton__row"
        :style="rowGridStyle"
      >
        <div
          v-for="c in columnCount"
          :key="`sk-cell-${n}-${c}`"
          class="pos-skeleton__cell"
        >
          <div
            class="pos-skeleton__line pos-skeleton__line--sm pos-skeleton__shimmer"
            :style="{ width: cellWidths[(c - 1 + n) % cellWidths.length] }"
          ></div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="pos-skeleton__grid"
      :class="`pos-skeleton__grid--${variant}`"
    >
      <div
        v-for="n in cardCount"
        :key="`sk-card-${n}`"
        class="pos-skeleton__card"
        :class="`pos-skeleton__card--${variant}`"
      >
        <template v-if="variant === 'product'">
          <div class="pos-skeleton__thumb pos-skeleton__shimmer"></div>
          <div class="pos-skeleton__card-body">
            <div class="pos-skeleton__line pos-skeleton__line--lg pos-skeleton__shimmer" style="width: 75%"></div>
            <div class="pos-skeleton__line pos-skeleton__line--sm pos-skeleton__shimmer mt-2" style="width: 45%"></div>
          </div>
        </template>
        <template v-else>
          <span class="pos-skeleton__rail"></span>
          <div class="pos-skeleton__r2-head">
            <div class="pos-skeleton__line pos-skeleton__line--lg pos-skeleton__shimmer" style="width: 68px"></div>
            <div class="pos-skeleton__r2-val pos-skeleton__shimmer"></div>
          </div>
          <div class="pos-skeleton__r2-divider"></div>
          <div class="pos-skeleton__r2-foot">
            <div class="pos-skeleton__r2-chip pos-skeleton__shimmer"></div>
            <div class="pos-skeleton__line pos-skeleton__line--sm pos-skeleton__shimmer" style="width: 56px"></div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="js">
export default {
  props: {
    variant: {
      type: String,
      default: 'table',
      validator: (v) => ['table', 'product', 'bills'].includes(v)
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    pillCount: {
      type: Number,
      default: 4
    },
    badgeCount: {
      type: Number,
      default: 3
    },
    cardCount: {
      type: Number,
      default: 8
    },
    summaryCardCount: {
      type: Number,
      default: 4
    },
    rowCount: {
      type: Number,
      default: 10
    },
    columnCount: {
      type: Number,
      default: 7
    }
  },
  data() {
    return {
      pillWidths: ['56px', '72px', '48px', '68px', '60px'],
      cellWidths: ['80%', '55%', '70%', '40%', '65%', '50%', '75%']
    }
  },
  computed: {
    rowGridStyle() {
      return { gridTemplateColumns: `repeat(${this.columnCount}, 1fr)` };
    }
  }
}
</script>

<style scoped>
.pos-skeleton__shimmer {
  background: linear-gradient(90deg, #eceff2 0%, #f5f7f9 50%, #eceff2 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.25s ease-in-out infinite;
  border-radius: 6px;
  display: inline-block;
}

.pos-skeleton__pills {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow: hidden;
  padding-bottom: 5px;
}

.pos-skeleton__pill {
  height: 36px;
  border-radius: 50rem;
  flex-shrink: 0;
}

.pos-skeleton__badges {
  display: flex;
  gap: 6px;
  align-items: center;
}

.pos-skeleton__badge {
  height: 20px;
  width: 70px;
  border-radius: 10px;
}

.pos-skeleton__search {
  display: flex;
  align-items: center;
  min-width: 260px;
}

.pos-skeleton__search-bar {
  width: 100%;
  height: 32px;
  border-radius: 6px;
}

.pos-skeleton__summary-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.04);
  height: 100%;
}

.pos-skeleton__line--xl {
  height: 28px;
}

.pos-skeleton__datatable {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.04);
}

.pos-skeleton__row {
  display: grid;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f3f5;
}

.pos-skeleton__row:last-child {
  border-bottom: none;
}

.pos-skeleton__row--head {
  background: #f8f9fa;
}

.pos-skeleton__cell {
  display: flex;
  align-items: center;
}

@media (max-width: 767px) {
  .pos-skeleton__row {
    grid-template-columns: repeat(3, 1fr);
  }
  .pos-skeleton__cell:nth-child(n + 4) {
    display: none;
  }
}

.pos-skeleton__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding-bottom: 80px;
}

.pos-skeleton__card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

/* --- R2 card skeleton (ทรงเดียวกับการ์ด session/table จริง) --- */
.pos-skeleton__card--table {
  position: relative;
  display: flex;
  flex-direction: column;
  align-self: start;
  border: 1px solid #ece8e2;
  border-radius: 15px;
  box-shadow: 0 1px 2px rgba(30, 25, 20, 0.05), 0 3px 10px rgba(30, 25, 20, 0.05);
  padding: 14px 15px 14px 18px;
}

.pos-skeleton__rail {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: #e6e2db;
}

.pos-skeleton__r2-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 34px;
}

.pos-skeleton__r2-val {
  width: 78px;
  height: 24px;
  border-radius: 50rem;
  flex-shrink: 0;
}

.pos-skeleton__r2-divider {
  height: 1px;
  background: #efece7;
  margin: 11px 0 10px;
}

.pos-skeleton__r2-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 26px;
}

.pos-skeleton__r2-chip {
  width: 104px;
  height: 22px;
  border-radius: 50rem;
  flex-shrink: 0;
}

.pos-skeleton__card--product {
  display: flex;
  flex-direction: column;
}

.pos-skeleton__thumb {
  width: 100%;
  height: 120px;
  border-radius: 0;
}

.pos-skeleton__card-body {
  padding: 12px;
}

.pos-skeleton__line {
  display: block;
  height: 12px;
  border-radius: 4px;
}

.pos-skeleton__line--lg {
  height: 20px;
}

.pos-skeleton__line--sm {
  height: 12px;
}

@keyframes pos-skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (min-width: 768px) {
  .pos-skeleton__grid--table {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }

  .pos-skeleton__grid--product {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
  }
}
</style>
